import React, { useState, useRef, useCallback, useEffect } from 'react';

export const DEFAULT_DRAWER_WIDTH = 560;
export const MIN_DRAWER_WIDTH = 360;
export const MAX_DRAWER_WIDTH = 1200;

export interface UseResizableDrawerResult {
  drawerWidth: number;
  isResizing: boolean;
  handlePointerDown: React.PointerEventHandler<HTMLDivElement>;
  handlePointerMove: React.PointerEventHandler<HTMLDivElement>;
  handlePointerUp: React.PointerEventHandler<HTMLDivElement>;
}

export function useResizableDrawer(isOpen: boolean): UseResizableDrawerResult {
  const [drawerWidth, setDrawerWidth] = useState(DEFAULT_DRAWER_WIDTH);
  const draggingWidthRef = useRef(DEFAULT_DRAWER_WIDTH);
  const [isResizing, setIsResizing] = useState(false);

  // Set CSS variable on mount (Pitfall 3 fix)
  useEffect(() => {
    document.documentElement.style.setProperty('--chat-drawer-width', `${DEFAULT_DRAWER_WIDTH}px`);
  }, []);

  // Reset to default on close (D-09, Pitfall 4 fix)
  useEffect(() => {
    if (!isOpen) {
      setDrawerWidth(DEFAULT_DRAWER_WIDTH);
      draggingWidthRef.current = DEFAULT_DRAWER_WIDTH;
      document.documentElement.style.setProperty(
        '--chat-drawer-width',
        `${DEFAULT_DRAWER_WIDTH}px`,
      );
    }
  }, [isOpen]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      draggingWidthRef.current = drawerWidth;
      setIsResizing(true);
    },
    [drawerWidth],
  );

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
    // Drawer is on the right; dragging handle left (negative movementX) increases width
    const next = Math.max(
      MIN_DRAWER_WIDTH,
      Math.min(MAX_DRAWER_WIDTH, draggingWidthRef.current - e.movementX),
    );
    draggingWidthRef.current = next;
    // Direct DOM mutation — no React render (D-02)
    document.documentElement.style.setProperty('--chat-drawer-width', `${next}px`);
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.releasePointerCapture(e.pointerId);
    setIsResizing(false);
    setDrawerWidth(draggingWidthRef.current); // commit once (D-02)
  }, []);

  return {
    drawerWidth,
    isResizing,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  };
}
